import {
  curvedLine,
  curveGeometry,
  curveTextPaint,
  curveTextShadow,
  estimateTextLength,
  highlightCss,
  isRichText,
  type TextElement,
  textParagraphs,
} from "@cva/shared";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { GradientDef } from "./shape";
import { fontFamilyCss, textContentStyle } from "./styles";

/**
 * Text effects that need their own markup: the highlight box behind each
 * line and curved text (docs/04-dokumentformat.md). The CSS-only effects are
 * part of textContentStyle, so the inline editor shows them too.
 */

/** Wraps a line's content in the highlight box (no effect: unchanged). */
export function withHighlight(element: TextElement, content: ReactNode): ReactNode {
  const css = highlightCss(element);
  if (!css) return content;
  return <span style={css as CSSProperties}>{content}</span>;
}

interface CurveRun {
  text: string;
  style: CSSProperties;
  fill?: string;
}

/** Runs of the text on one line; marks keep font, weight, style, size and colour. */
function curveRuns(element: TextElement): CurveRun[] {
  const upper = (text: string) => (element.style.uppercase ? text.toLocaleUpperCase("de") : text);
  if (!isRichText(element)) return [{ text: upper(curvedLine(element.text)), style: {} }];
  const runs: CurveRun[] = [];
  textParagraphs(element).forEach((paragraph, index) => {
    if (index > 0) runs.push({ text: " ", style: {} });
    for (const run of paragraph.runs) {
      const style: CSSProperties = {};
      if (run.style.fontFamily) style.fontFamily = fontFamilyCss(run.style.fontFamily);
      if (run.style.fontSize !== undefined) style.fontSize = run.style.fontSize;
      if (run.style.fontWeight !== undefined) style.fontWeight = run.style.fontWeight;
      if (run.style.italic !== undefined) style.fontStyle = run.style.italic ? "italic" : "normal";
      runs.push({ text: upper(run.text), style, fill: run.style.color });
    }
  });
  return runs;
}

/**
 * Text along a circular arc (SVG textPath). The browser measures the
 * straight line first, then the arc is sized so the text fits exactly.
 */
export function CurvedText({ element }: { element: TextElement }) {
  const measureRef = useRef<SVGTextElement>(null);
  const runs = curveRuns(element);
  const line = runs.map((run) => run.text).join("");
  const { style } = element;
  const [measured, setMeasured] = useState<number | null>(null);
  const [fontTick, setFontTick] = useState(0);
  const rawId = useId();
  const id = `curve${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;

  useEffect(() => {
    if (typeof document === "undefined" || !document.fonts) return;
    const onLoad = () => setFontTick((tick) => tick + 1);
    document.fonts.addEventListener("loadingdone", onLoad);
    return () => document.fonts.removeEventListener("loadingdone", onLoad);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the text, its font or loaded fonts change
  useLayoutEffect(() => {
    const node = measureRef.current;
    if (!node) return;
    const length = node.getComputedTextLength();
    if (length > 0 && (measured === null || Math.abs(length - measured) > 0.5)) {
      setMeasured(length);
    }
  }, [line, style, element.marks, fontTick, measured]);

  const length = measured ?? estimateTextLength(line, style.fontSize, style.letterSpacing || 0);
  const geometry = curveGeometry(length, element.curve ?? 0, style.fontSize);
  const gradient = element.effect?.type === "gradient" ? element.effect.fill : null;
  const paint = curveTextPaint(element, gradient ? `${id}g` : undefined);
  const base = textContentStyle(element);
  const textStyle: CSSProperties = {
    fontFamily: base.fontFamily,
    fontSize: base.fontSize,
    fontWeight: base.fontWeight,
    fontStyle: base.fontStyle,
    fontSynthesis: "none",
    letterSpacing: base.letterSpacing,
    textDecorationLine: base.textDecorationLine,
    textShadow: curveTextShadow(element),
    paintOrder: paint.paintOrder,
    whiteSpace: "pre",
  };
  const plainFill = paint.fill === style.color;
  const tspans = runs.map((run, index) => (
    <tspan
      // biome-ignore lint/suspicious/noArrayIndexKey: runs are positional
      key={index}
      style={run.style}
      fill={run.fill && plainFill ? run.fill : undefined}
    >
      {run.text}
    </tspan>
  ));
  return (
    <div
      data-text-content=""
      style={{
        position: "relative",
        margin: 0,
        padding: 0,
        height: geometry.height,
        flexShrink: 0,
      }}
    >
      <svg
        aria-label={line}
        role="img"
        width={geometry.width}
        height={geometry.height}
        viewBox={`0 0 ${geometry.width} ${geometry.height}`}
        style={{
          position: "absolute",
          left: (element.width - geometry.width) / 2,
          top: 0,
          overflow: "visible",
        }}
      >
        <defs>
          <path id={id} d={geometry.path} fill="none" />
          {gradient ? (
            <GradientDef
              id={`${id}g`}
              gradient={gradient}
              width={geometry.width}
              height={geometry.height}
            />
          ) : null}
        </defs>
        <text ref={measureRef} visibility="hidden" style={textStyle} x={0} y={0}>
          {tspans}
        </text>
        <text
          style={textStyle}
          fill={paint.fill}
          stroke={paint.stroke}
          strokeWidth={paint.strokeWidth}
          strokeLinejoin="round"
        >
          <textPath href={`#${id}`} startOffset="50%" textAnchor="middle">
            {tspans}
          </textPath>
        </text>
      </svg>
    </div>
  );
}
